import './search-results.scss';

import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Text } from '@sola/ark-core/atoms/text/text.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import { trackSelectContent } from '@sola/cookies/components/datalayer.js';

import { CardList } from '../../molecules/card-list/card-list';
import { Card } from '../../molecules/card/card';
import { Pagination } from '../../molecules/pagination/pagination';
import { useSearchAPI, useUrlState } from '../../utils/search';

type SearchResultsListProps = {
  queryText: string;
  itemsPerPage: number;
  currentPage: number;
  itemOffset: number;
  onPageChange: (page: number) => void;
};

const SearchResultsSchema = z.object({
  q: z.string().optional(),
  page: z.coerce.number().int().nonnegative().catch(0).optional(),
});

const SearchResultsText = ({
  queryText,
  total,
  resultsPerPage,
  currentPage,
}: {
  queryText: string;
  total?: number;
  resultsPerPage: number;
  currentPage: number;
}) => {
  const resultsRemainder = currentPage * resultsPerPage + resultsPerPage;
  const noSearch = queryText.length === 0;
  const hasResults = !!total && total > 0 && queryText.length > 0;

  return (
    <Text>
      <p>
        {hasResults || noSearch ? (
          <Trans
            i18nKey={`skippy.article.${queryText.length === 0 ? 'resultsWithPagination' : 'resultsWithPaginationQuery'}`}
            values={{
              start: currentPage * resultsPerPage + 1,
              end: resultsRemainder > total! ? total : resultsRemainder,
              total,
              query: queryText,
            }}
            components={{ 1: <strong /> }}
          />
        ) : (
          <Trans
            i18nKey="skippy.article.noResults"
            values={{
              query: queryText,
            }}
            components={{ 1: <strong /> }}
          />
        )}
      </p>
    </Text>
  );
};

export const SearchResultsList = ({
  queryText,
  itemsPerPage,
  currentPage,
  itemOffset,
  onPageChange,
}: SearchResultsListProps) => {
  const { searchParams } = useUrlState(SearchResultsSchema);
  const { t } = useTranslation(['common']);

  const list = useSearchAPI(queryText, itemOffset, itemsPerPage, searchParams);
  const hasResults = !!list && !!list?.totalCount && list.totalCount > 0;

  const getHighlightedText = (
    text: string | undefined | null,
    highlight: string,
  ) => {
    if (!text) {
      return null;
    }
    const parts = text.split(
      new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'),
    );
    return (
      <span>
        {parts.map((part, i) => (
          <span
            key={i}
            className={
              part.toLowerCase() === highlight.toLowerCase()
                ? 'SearchResults--highlight'
                : ''
            }
          >
            {part}
          </span>
        ))}
      </span>
    );
  };
  if (!list && queryText.length > 0) {
    return <Alert variant="negative" message={t('skippy.siteSearch.error')} />;
  }

  return (
    <>
      {hasResults && queryText.length > 0 && (
        <>
          <SearchResultsText
            queryText={queryText}
            total={list?.totalCount}
            resultsPerPage={itemsPerPage}
            currentPage={currentPage}
          />
          <CardList columns={1} columnsMd={1} columnsLg={1}>
            {list.items.map((item) => (
              <Card
                key={item.contentfulEntryID}
                title={item.pageTitle}
                body={<p>{getHighlightedText(item.summary, queryText)}</p>}
                variant="listItem"
                primaryCTA={
                  item.path
                    ? {
                        title: 'Read more',
                        link: item.path,
                      }
                    : undefined
                }
                onClick={() =>
                  trackSelectContent(
                    item.contentfulEntryID!,
                    item.pageTitle!,
                    item.contentfulType!,
                    [
                      ...(item.taxonomy?.articleTopics ?? []),
                      ...(item.taxonomy?.articleThemes ?? []),
                    ],
                  )
                }
              />
            ))}
          </CardList>
          <Pagination
            itemsPerPage={itemsPerPage}
            totalItems={list.totalCount || 0}
            onPageChange={onPageChange}
            initialPage={currentPage}
          />
        </>
      )}
      {!hasResults && queryText.length > 0 && (
        <Text>
          <p>
            <Trans
              i18nKey="skippy.article.noResults"
              values={{
                query: queryText,
              }}
              components={{ 1: <strong /> }}
            />
          </p>
        </Text>
      )}
    </>
  );
};
